import React from 'react';
import { getExerciseProgressions } from '../../utils/progressions';
import PlaceholderImage from '../PlaceholderImage';

const ExerciseSlot = ({ exercise, slotIndex, onSlotClick, onRemoveExercise, onShowSuggestions, onRemoveSlot, canRemoveSlot = false, isSelected = false, isShowingProgressions = false }) => {
  const hasProgressions = exercise ? getExerciseProgressions(exercise.id) : null;

  if (exercise) {
    return (
      <div className={`border rounded-lg p-4 bg-white hover:border-blue-300 transition-colors ${isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}>
        {/* Exercise Content */}
        <div className="flex flex-col space-y-3">
          {/* Top Row: Slot Number, Image, Info, Actions */}
          <div className="flex items-center space-x-4">
            {/* Slot Number */}
            <div className="flex-shrink-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-gray-600">{slotIndex + 1}</span>
            </div>

            {/* Exercise Image */}
            <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
              {exercise.images && exercise.images.length > 0 ? (
                exercise.images[0] === "placeholder.jpg" ? (
                  <PlaceholderImage 
                    className="w-full h-full"
                  />
                ) : (
                  <img
                    src={`/exercise-images/${exercise.images[0]}`}
                    alt={exercise.name}
                    className="w-full h-full object-cover"
                  />
                )
              ) : (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <span className="text-gray-400 text-sm">📷</span>
                </div>
              )}
            </div>

            {/* Exercise Info */}
            <div className="flex-1 min-w-0">
              <h3 className="font-medium text-gray-900 text-sm sm:text-base">
                {exercise.name}
              </h3>
              <p className="text-sm text-gray-500">
                {exercise.primaryMuscles && exercise.primaryMuscles.length > 0
                  ? exercise.primaryMuscles[0]
                  : 'Multiple muscles'
                }
              </p>
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-2">
              {/* Progression Suggestions Button */}
              {hasProgressions && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onShowSuggestions();
                  }}
                  className={`px-3 py-2 transition-all duration-200 rounded-lg border-2 font-medium text-sm flex items-center gap-2 ${
                    isShowingProgressions 
                      ? 'text-blue-600 bg-blue-50 border-blue-300 shadow-md' 
                      : 'text-blue-600 bg-blue-50 border-blue-200 hover:bg-blue-100 hover:border-blue-300 hover:shadow-sm'
                  }`}
                  title="View progression suggestions"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span className="hidden sm:inline">Progressions</span>
                </button>
              )}

              {/* Remove Exercise Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveExercise();
                }}
                className="p-2 text-gray-400 hover:text-red-500 transition-colors"
                title="Remove exercise"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {/* Remove Slot Button */}
              {canRemoveSlot && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveSlot();
                  }}
                  className="p-2 text-gray-400 hover:text-orange-500 transition-colors"
                  title="Remove slot"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Bottom Row: Rep Range Display */}
          {exercise.repRange && (
            <div className="flex flex-wrap gap-1 ml-20">
              {Object.entries(exercise.repRange).slice(0, 2).map(([goal, repRangeString]) => {
                const getIcon = (goal) => {
                  const icons = {
                    'strength': '💪',
                    'muscle_building': '🏋️',
                    'endurance': '🏃',
                    'power': '⚡',
                    'flexibility': '🧘',
                    'mobility': '🔄',
                    'recovery': '😌',
                    'cardio': '❤️',
                    'hiit': '🔥',
                    'explosive': '💥',
                    'technique': '🎯',
                    'stability': '⚖️',
                    'activation': '🔋'
                  };
                  return icons[goal] || '💪';
                };

                const getGoalLabel = (goal) => {
                  const labels = {
                    'strength': 'Strength',
                    'muscle_building': 'Muscle Building',
                    'endurance': 'Endurance',
                    'power': 'Power',
                    'flexibility': 'Flexibility',
                    'mobility': 'Mobility',
                    'recovery': 'Recovery',
                    'cardio': 'Cardio',
                    'hiit': 'HIIT',
                    'explosive': 'Explosive',
                    'technique': 'Technique',
                    'stability': 'Stability',
                    'activation': 'Activation'
                  };
                  return labels[goal] || goal;
                };

                // Extract sets and reps from the string format "3-5 sets x 4-8 reps"
                const extractSetsReps = (repRangeString) => {
                  const match = repRangeString.match(/(\d+-\d+)\s*sets?\s*x\s*(\d+-\d+)\s*reps?/);
                  if (match) {
                    return `${match[1]}x${match[2]}`;
                  }
                  // Fallback for other formats
                  return repRangeString.replace(/\s*sets?\s*x\s*/g, 'x').replace(/\s*reps?/g, '');
                };

                return (
                  <span
                    key={goal}
                    className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800"
                    title={`${getGoalLabel(goal)}: ${repRangeString}`}
                  >
                    <span className="mr-1">{getIcon(goal)}</span>
                    <span>{extractSetsReps(repRangeString)}</span>
                  </span>
                );
              })}
            </div>
          )}
        </div>
      </div>
    );
  }

  // Empty slot
  return (
    <div
      onClick={onSlotClick}
      className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer ${isSelected ? 'border-blue-400 bg-blue-100' : 'border-gray-300 bg-gray-50 hover:border-blue-400 hover:bg-blue-50'}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-center space-y-3 sm:space-y-0 sm:space-x-4">
        {/* Slot Number */}
        <div className="flex-shrink-0 w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center">
          <span className="text-sm font-medium text-gray-500">{slotIndex + 1}</span>
        </div>

        {/* Add Icon */}
        <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center">
          <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>

        {/* Add Exercise Text */}
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-500">Add Exercise</p>
          <p className="text-xs text-gray-400 hidden sm:block">Click to search and add an exercise</p>
        </div>

        {/* Remove Slot Button for empty slots */}
        {canRemoveSlot && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onRemoveSlot();
            }}
            className="p-2 text-gray-400 hover:text-orange-500 transition-colors"
            title="Remove slot"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        )}
      </div>
    </div>
  );
};

export default ExerciseSlot;